import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import usmApi from "@/lib/usmApi";
import { PageHeader } from "../UsmLayout";
import {
  ArrowLeft, Package, ShoppingCart, ArrowRightLeft, ScanBarcode, Printer,
  ClipboardEdit, Eye, AlertCircle, RotateCcw, Tag,
} from "lucide-react";

// Maps a unified-timeline event `type` (movement OR audit) → presentation.
const TYPE_META = {
  // movements
  created:          { label: "Stoğa Alındı",       Icon: Package,        cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  adjusted:         { label: "Bilgiler Güncellendi", Icon: ClipboardEdit, cls: "bg-stone-50 text-stone-700 ring-stone-200" },
  sale:             { label: "Satıldı",            Icon: ShoppingCart,   cls: "bg-blue-50 text-blue-700 ring-blue-200" },
  sale_void:        { label: "Satış İptal",        Icon: RotateCcw,      cls: "bg-amber-50 text-amber-700 ring-amber-200" },
  transfer_out:     { label: "Transfer Çıkışı",    Icon: ArrowRightLeft, cls: "bg-cyan-50 text-cyan-700 ring-cyan-200" },
  transfer_in:      { label: "Transfer Girişi",    Icon: ArrowRightLeft, cls: "bg-cyan-50 text-cyan-700 ring-cyan-200" },
  transfer_cancel:  { label: "Transfer İptal",     Icon: RotateCcw,      cls: "bg-amber-50 text-amber-700 ring-amber-200" },
  // audit-only event types we care about
  "inventory.status_changed":  { label: "Durum Değiştirildi", Icon: AlertCircle,  cls: "bg-purple-50 text-purple-700 ring-purple-200" },
  "inventory.ean8_assigned":   { label: "EAN-8 Barkodu Atandı", Icon: ScanBarcode, cls: "bg-amber-50 text-amber-700 ring-amber-200" },
  "label.printed":             { label: "Etiket Yazdırıldı",  Icon: Printer,     cls: "bg-stone-50 text-stone-700 ring-stone-200" },
  "inventory.price_lookup":    { label: "Fiyat Sorgulandı",   Icon: Eye,         cls: "bg-stone-50 text-stone-700 ring-stone-200" },
  "inventory.deleted":         { label: "Silindi",            Icon: AlertCircle, cls: "bg-red-50 text-red-700 ring-red-200" },
};

const STATUS_LABEL = {
  active: "Aktif", reserved: "Rezerve", sold: "Satıldı",
  repair: "Tamirde", transfer: "Transferde", lost: "Kayıp",
};

const FMT_TRY = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 0 });

export default function Timeline() {
  const { iid } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let aborted = false;
    (async () => {
      setLoading(true);
      try {
        const r = await usmApi.get(`/inventory/${iid}/timeline?limit=500`);
        if (!aborted) setData(r.data);
      } catch (e) {
        if (!aborted) setError(e?.response?.data?.detail || "Yüklenemedi");
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [iid]);

  const item = data?.item;
  const events = data?.events || [];
  const counts = data?.counts || {};

  // Group events by day for a calendar-like timeline.
  const byDay = useMemo(() => {
    const groups = new Map();
    for (const e of events) {
      const day = (e.at || "").slice(0, 10);
      if (!groups.has(day)) groups.set(day, []);
      groups.get(day).push(e);
    }
    return Array.from(groups.entries()); // [[day, events[]], ...]
  }, [events]);

  return (
    <div data-testid="inventory-timeline">
      <PageHeader
        title="Ürün Zaman Çizelgesi"
        subtitle={item ? `${item.name} · ${item.stock_code || item.barcode}` : "Tüm hareketler"}
        actions={
          <Link to="/usm/inventory" data-testid="back-to-inventory"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-[#E7E5E4] hover:bg-[#FAFAF9]">
            <ArrowLeft size={14} /> Envantere Dön
          </Link>
        }
      />

      {/* Item summary card */}
      {item && (
        <div className="bg-white border border-[#E7E5E4] p-5 mb-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <KV label="Stok Kodu" value={item.stock_code} mono />
          <KV label="EAN-8 Barkod" value={item.ean8_barcode || "—"} mono />
          <KV label="Durum" value={STATUS_LABEL[item.status] || item.status} />
          <KV label="Oluşturuldu" value={fmtShort(item.created_at)} />
        </div>
      )}

      {/* Counts pill */}
      <div className="flex flex-wrap items-center gap-2 mb-4 text-[12px]">
        <span className="px-2.5 py-1 bg-[#FAFAF9] ring-1 ring-[#E7E5E4]">Toplam: <strong>{counts.total ?? 0}</strong></span>
        <span className="px-2.5 py-1 bg-[#FAFAF9] ring-1 ring-[#E7E5E4]">Hareketler: <strong>{counts.movements ?? 0}</strong></span>
        <span className="px-2.5 py-1 bg-[#FAFAF9] ring-1 ring-[#E7E5E4]">Denetim Kayıtları: <strong>{counts.audits ?? 0}</strong></span>
      </div>

      {/* Body */}
      <div className="bg-white border border-[#E7E5E4]">
        {loading && <p data-testid="timeline-loading" className="p-6 text-sm text-[#71717A]">Yükleniyor…</p>}
        {error && !loading && <p data-testid="timeline-error" className="p-6 text-sm text-red-600">{error}</p>}
        {!loading && !error && events.length === 0 && (
          <p data-testid="timeline-empty" className="p-6 text-sm text-[#71717A]">Henüz kayıt yok.</p>
        )}

        {!loading && !error && events.length > 0 && (
          <div className="px-5 py-6 sm:px-8 sm:py-8">
            {byDay.map(([day, evs]) => (
              <div key={day} className="mb-8 last:mb-0">
                <div className="sticky top-0 z-10 -mx-5 sm:-mx-8 px-5 sm:px-8 py-2 bg-white/95 backdrop-blur border-b border-[#F5F5F4]">
                  <div className="text-[11px] uppercase tracking-wider text-[#A1A1AA] font-mono">{fmtDay(day)}</div>
                </div>
                <ol className="relative border-l-2 border-[#E7E5E4] ml-3 mt-4 space-y-5">
                  {evs.map((ev, i) => <EventRow key={`${day}-${i}`} ev={ev} />)}
                </ol>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EventRow({ ev }) {
  const meta = TYPE_META[ev.type] || { label: ev.type, Icon: ClipboardEdit, cls: "bg-stone-50 text-stone-700 ring-stone-200" };
  const Icon = meta.Icon;
  return (
    <li data-testid={`event-${ev.type}`} className="ml-5 relative">
      <span className={`absolute -left-[28px] top-1 h-4 w-4 ring-2 ring-white rounded-full ${meta.cls.split(" ")[0]}`} />
      <div className="flex items-baseline gap-3 flex-wrap">
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] ring-1 ${meta.cls}`}>
          <Icon size={11} /> {meta.label}
        </span>
        <span className="text-[11px] font-mono text-[#A1A1AA]">{fmtTime(ev.at)}</span>
      </div>
      <div className="mt-1.5 text-[13px] text-[#27272A] leading-relaxed">
        <EventBody ev={ev} />
      </div>
      <div className="mt-1 text-[11.5px] text-[#71717A]">
        {ev.actor?.full_name || ev.actor?.username || "—"}
      </div>
    </li>
  );
}

function EventBody({ ev }) {
  // Movement-sourced rich content
  if (ev.source === "movement") {
    if (ev.type === "sale") {
      return (
        <div className="flex flex-wrap items-baseline gap-2">
          <span>Satış</span>
          {ev.sale?.sale_no && (
            <Link to={`/usm/sales/${ev.ref_id}`} className="font-mono text-[#C8A96A] hover:underline">
              {ev.sale.sale_no}
            </Link>
          )}
          {ev.amount != null && <span className="text-[#71717A]">· {FMT_TRY.format(ev.amount)}</span>}
        </div>
      );
    }
    if (ev.type === "sale_void") {
      return (
        <div className="flex flex-wrap items-baseline gap-2">
          <span>Satış geri alındı</span>
          {ev.sale?.sale_no && (
            <Link to={`/usm/sales/${ev.ref_id}`} className="font-mono text-[#C8A96A] hover:underline">{ev.sale.sale_no}</Link>
          )}
        </div>
      );
    }
    if (ev.type === "transfer_out" || ev.type === "transfer_in" || ev.type === "transfer_cancel") {
      return (
        <div className="flex flex-wrap items-baseline gap-2">
          {ev.from_branch && <span className="font-mono text-[#71717A]">{ev.from_branch.code || ev.from_branch.name}</span>}
          <ArrowRightLeft size={11} className="text-[#A1A1AA]" />
          {ev.to_branch && <span className="font-mono text-[#71717A]">{ev.to_branch.code || ev.to_branch.name}</span>}
          {ev.transfer?.transfer_no && (
            <Link to={`/usm/transfers/${ev.ref_id}`} className="font-mono text-[#C8A96A] hover:underline">
              {ev.transfer.transfer_no}
            </Link>
          )}
        </div>
      );
    }
    if (ev.type === "created") {
      return (
        <div className="flex flex-wrap items-baseline gap-2">
          <span>Stoğa alındı</span>
          {ev.to_branch && <span className="text-[#71717A]">· {ev.to_branch.name}</span>}
          {ev.amount != null && <span className="text-[#71717A]">· {FMT_TRY.format(ev.amount)}</span>}
        </div>
      );
    }
    if (ev.note) return <span>{ev.note}</span>;
    return <span className="text-[#A1A1AA]">—</span>;
  }

  // Audit-sourced
  if (ev.type === "inventory.status_changed") {
    const oldS = STATUS_LABEL[ev.old?.status] || ev.old?.status;
    const newS = STATUS_LABEL[ev.new?.status] || ev.new?.status;
    return (
      <div className="flex flex-wrap items-baseline gap-1.5">
        <span className="text-[#71717A] line-through">{oldS || "—"}</span>
        <span className="text-[#A1A1AA]">→</span>
        <strong>{newS || "—"}</strong>
        {ev.extra?.reason && <span className="text-[#71717A]">· {ev.extra.reason}</span>}
      </div>
    );
  }
  if (ev.type === "inventory.ean8_assigned") {
    return <span className="font-mono">{ev.new?.ean8_barcode}</span>;
  }
  if (ev.type === "label.printed") {
    return <span>{ev.extra?.profile_name || "Etiket"}</span>;
  }
  if (ev.type === "inventory.price_lookup") {
    return <span className="text-[#71717A]">Kod: <span className="font-mono">{ev.extra?.code_used || "—"}</span></span>;
  }
  if (Array.isArray(ev.fields) && ev.fields.length > 0) {
    return <span className="text-[#71717A]">{ev.fields.length} alan</span>;
  }
  return <span className="text-[#A1A1AA]">—</span>;
}

function KV({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider text-[#A1A1AA]">{label}</div>
      <div className={`text-[13.5px] mt-0.5 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function fmtShort(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}
function fmtDay(day) {
  if (!day) return "";
  try {
    return new Date(day + "T00:00:00Z").toLocaleDateString("tr-TR", {
      weekday: "long", day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return day; }
}
function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return iso; }
}
